import axios from "axios";
import { API } from "../App";

/**
 * Fetch FedEx shipping rates from the CheepSweepz backend.
 *
 * @param {Object} packageData
 * @param {string} packageData.destinationCountry  ISO-2 country code (e.g. "US")
 * @param {string} [packageData.destinationPostal] Postal / ZIP code
 * @param {string} [packageData.destinationCity]   City name
 * @param {number} packageData.weightKg            Package weight in kg
 * @param {number} [packageData.lengthCm]          Length in cm (default 30)
 * @param {number} [packageData.widthCm]           Width in cm  (default 20)
 * @param {number} [packageData.heightCm]          Height in cm (default 15)
 * @returns {Promise<{ provider: string, sandbox: boolean, origin: string, destination: string, rates: Array }>}
 */
const SHIPPING_HEADERS = {
  "X-DHC-Hub": "digitalhustlerscaribbean.com",
  "X-Site-Origin":
    typeof window !== "undefined" ? window.location.origin : "https://cheepsweepz.shop",
};

export async function fetchFedExRates(packageData) {
  const {
    destinationCountry,
    destinationPostal = "",
    destinationCity = "",
    originCountry,
    sourceUrl,
    marketplace,
    conciergeTier,
    weightKg,
    lengthCm = 30,
    widthCm = 20,
    heightCm = 15,
  } = packageData;

  const payload = {
    destination_country: destinationCountry,
    destination_postal: destinationPostal,
    destination_city: destinationCity,
    weight_kg: weightKg,
    length_cm: lengthCm,
    width_cm: widthCm,
    height_cm: heightCm,
  };

  if (originCountry) payload.origin_country = originCountry;
  if (sourceUrl) payload.source_url = sourceUrl;
  if (marketplace) payload.marketplace = marketplace;
  if (conciergeTier) payload.concierge_tier = conciergeTier;

  const { data } = await axios.post(`${API}/shipping/rates`, payload, {
    withCredentials: true,
    headers: SHIPPING_HEADERS,
  });

  return data;
}

/** Supplier pickup markets for cross-country import lanes. */
export async function fetchPickupOrigins() {
  try {
    const { data } = await axios.get(`${API}/shipping/destinations`, {
      params: { scope: "pickup" },
      headers: SHIPPING_HEADERS,
    });
    if (Array.isArray(data?.destinations) && data.destinations.length > 0) {
      return data.destinations;
    }
  } catch {
    // Fall through to static catalog
  }
  return null;
}

/**
 * Fetch all proven industrial inventory presets with verified-delivery stats.
 */
export async function fetchInventoryPresets() {
  const { data } = await axios.get(`${API}/inventory/presets`);
  return data;
}

/**
 * Fetch verified-delivery counts for the "Verified Delivery" badge.
 */
export async function fetchVerifiedDeliveries() {
  const { data } = await axios.get(`${API}/inventory/verified-deliveries`);
  return data;
}

/**
 * Fetch a public all-inclusive landed-cost quote (no margin breakdown).
 */
export async function fetchProfitabilityQuote(opts) {
  const {
    skuKey,
    unitCostUsd,
    weightKg,
    dutyCategory,
    shipMode,
    quantity = 1,
    includeConcierge = true,
  } = opts;

  const { data } = await axios.post(
    `${API}/inventory/profitability-quote`,
    {
      sku_key: skuKey || null,
      unit_cost_usd: unitCostUsd || null,
      weight_kg: weightKg || null,
      duty_category: dutyCategory || null,
      ship_mode: shipMode || null,
      quantity,
      include_concierge: includeConcierge,
    },
    {
      withCredentials: false,
      timeout: 20000,
      headers: {
        "X-DHC-Hub": "digitalhustlerscaribbean.com",
        "X-Site-Origin":
          typeof window !== "undefined" ? window.location.origin : "https://cheepsweepz.shop",
      },
    }
  );

  return {
    totalLandedCost: data.totalLandedCost,
    costPerUnit: data.costPerUnit,
    quantity: data.quantity,
    estimatedDelivery: data.estimatedDelivery,
    isFullyCleared: data.isFullyCleared,
  };
}

export async function fetchPickupQuote(opts) {
  const {
    destinationCountry,
    destinationPostal = "",
    destinationCity = "",
    destinationState = "",
    originCountry,
    sourceUrl,
    marketplace,
    streetLines = [""],
    weightKg,
    shippingPreference = "DIRECT",
    lengthCm = 30,
    widthCm = 20,
    heightCm = 15,
    conciergeTier,
  } = opts;

  const payload = {
    destination_country: destinationCountry,
    destination_postal: destinationPostal,
    destination_city: destinationCity,
    destination_state: destinationState,
    street_lines: streetLines,
    weight_kg: weightKg,
    shipping_preference: shippingPreference,
    length_cm: lengthCm,
    width_cm: widthCm,
    height_cm: heightCm,
  };

  if (originCountry) payload.origin_country = originCountry;
  if (sourceUrl) payload.source_url = sourceUrl;
  if (marketplace) payload.marketplace = marketplace;
  if (conciergeTier) payload.concierge_tier = conciergeTier;

  const { data } = await axios.post(`${API}/shipping/pickup-quote`, payload, {
    withCredentials: true,
    headers: SHIPPING_HEADERS,
  });

  return data;
}
